import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { useAccount } from "../../app/session";
import { Dialog, PillView, useAction, useRueckfrage } from "../../components/ui";
import { date, time } from "../../lib/format";
import { pille } from "./konsole-model";
import {
  ARTEN, addActivity, artLabel, deleteActivity, kuerzel, loadKundenakte, loadLead, loadLeads, loadStaff, loadStart, LEADS_KEY, proposeDiscount,
  setStage, START_KEY, STAFF_KEY, STUFEN, stufeLabel, stufeTon, taskDone, type ActivityRow, type Art, type StaffKurz, type Stufe,
} from "./crm-data";

/**
 * Bausteine des Vertriebs in der Konsole (Vertrieb-Konsole, Inhaber 02.11.2026): Abfragen, Stufe, Zuständig, Verlauf,
 * schnelles Erfassen, „Verloren“ mit Grund und Rabatt-Vorschlag. Die Datenbank prüft jede Aktion (crm_*, 0460).
 */

export const useLeads = () => useQuery({ queryKey: LEADS_KEY, queryFn: () => loadLeads(api) });
export const useLead = (id: string) => useQuery({ queryKey: ["crm_leads", id, "betrieb"], queryFn: () => loadLead(api, id) });
export const useStaff = () => useQuery({ queryKey: STAFF_KEY, queryFn: () => loadStaff(api), staleTime: 5 * 60_000 });
export const useStart = () => useQuery({ queryKey: START_KEY, queryFn: () => loadStart(api), refetchInterval: 5 * 60_000 });
export const useKundenakte = (orgId: string) => useQuery({ queryKey: ["crm_account", orgId, "betrieb"], queryFn: () => loadKundenakte(api, orgId) });

/** Eigene Kennung (für „Meine“ und „Mir zuweisen“) */
export function useIch(): string | null {
  return useAccount().account?.session.userId ?? null;
}

export function StufePill({ stufe }: { stufe: string }) {
  return <PillView pill={pille(stufeLabel(stufe), stufeTon(stufe))} />;
}

export function staffName(staff: readonly StaffKurz[] | undefined, id: string | null): string {
  if (!id) return "ohne Zuständigen";
  const s = staff?.find((x) => x.user_id === id);
  return s ? `${s.name}${s.active ? "" : " (deaktiviert)"}` : "unbekannt";
}

export function OwnerKuerzel({ staff, id }: { staff: readonly StaffKurz[] | undefined; id: string | null }) {
  const name = staffName(staff, id);
  return <span className={`tk-crm-owner${id ? "" : " tk-crm-owner-leer"}`} title={`Zuständig: ${name}`} aria-label={`Zuständig: ${name}`}>{id ? kuerzel(name) : "–"}</span>;
}

/** Auswahl der zuständigen Person (nur aktive Mitglieder des Teams; die bisherige bleibt sichtbar, auch wenn deaktiviert) */
export function OwnerSelect({ value, onChange, disabled, label = "Zuständig" }: { value: string | null; onChange: (id: string) => void; disabled?: boolean; label?: string }) {
  const staff = useStaff();
  const liste = (staff.data ?? []).filter((s) => s.active || s.user_id === value);
  return (
    <label>{label}
      <select value={value ?? ""} disabled={disabled || !staff.data} onChange={(e) => { if (e.target.value) onChange(e.target.value); }}>
        {!value && <option value="">ohne Zuständigen</option>}
        {liste.map((s) => <option key={s.user_id} value={s.user_id} disabled={!s.active}>{s.name}{s.me ? " (ich)" : ""}{s.active ? "" : " (deaktiviert)"}</option>)}
      </select>
    </label>
  );
}

/** Stufe wählen; „Verloren“ fragt nach dem Grund */
export function StufeWahl({ leadId, stufe, disabled }: { leadId: string; stufe: Stufe; disabled?: boolean }) {
  const { busy, run } = useAction();
  const [verloren, setVerloren] = useState(false);
  return (
    <>
      <label>Stufe
        <select value={stufe} disabled={disabled || !!busy} onChange={(e) => {
          const next = e.target.value as Stufe;
          if (next === "verloren") setVerloren(true);
          else void run("stufe", () => setStage(api, leadId, next), `Stufe: ${stufeLabel(next)}.`);
        }}>
          {STUFEN.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </label>
      <VerlorenDialog leadId={verloren ? leadId : null} onClose={() => setVerloren(false)} />
    </>
  );
}

export function VerlorenDialog({ leadId, onClose }: { leadId: string | null; onClose: () => void }) {
  const { busy, error, run } = useAction();
  const [grund, setGrund] = useState("");
  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (!leadId) return;
    const ok = await run("verloren", () => setStage(api, leadId, "verloren", grund).then(() => true), "Als verloren markiert.");
    if (ok) { setGrund(""); onClose(); }
  }
  return (
    <Dialog open={!!leadId} onClose={onClose} title="Lead als verloren markieren">
      <form className="stack" onSubmit={(e) => void absenden(e)}>
        <label>Grund
          <input value={grund} maxLength={300} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. zu teuer, nutzt anderes Tool, keine Rückmeldung" />
        </label>
        <small className="muted">Der Grund steht im Verlauf und hilft bei der Auswertung. Ein neuer Kontakt (Anfrage, Buchung, Konto) holt den Lead automatisch zurück.</small>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button type="submit" className="primary" disabled={!!busy || grund.trim().length < 3} aria-busy={busy === "verloren"}>Als verloren markieren</button>
          <button type="button" onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </Dialog>
  );
}

/** Schnelles Erfassen: Notiz, Anruf, Mail, Termin oder Aufgabe (mit Fälligkeit und Zuständigem) */
export function SchnellErfassen({ leadId, ownerId }: { leadId: string; ownerId: string | null }) {
  const { busy, error, run } = useAction();
  const [art, setArt] = useState<Exclude<Art, "system">>("notiz");
  const [text, setText] = useState("");
  const [faellig, setFaellig] = useState("");
  const [fuer, setFuer] = useState<string | null>(ownerId);
  async function speichern(e: FormEvent) {
    e.preventDefault();
    const ok = await run("erfassen", () => addActivity(api, leadId, art, text, faellig || null, fuer).then(() => true), `${artLabel(art)} erfasst.`);
    if (ok) { setText(""); setFaellig(""); }
  }
  return (
    <form className="stack tk-crm-erfassen" onSubmit={(e) => void speichern(e)} aria-label="Schnell erfassen">
      <div className="row tk-crm-arten" role="group" aria-label="Art des Eintrags">
        {ARTEN.map((a) => <button key={a.key} type="button" aria-pressed={art === a.key} onClick={() => setArt(a.key)}>{a.label}</button>)}
      </div>
      <label className="sr-only" htmlFor={`erfassen-${leadId}`}>Text</label>
      <textarea id={`erfassen-${leadId}`} rows={3} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)}
        placeholder={art === "aufgabe" ? "Was ist zu tun?" : art === "anruf" ? "Worum ging es im Gespräch?" : art === "termin" ? "Termin und Ergebnis" : art === "mail" ? "Was wurde geschrieben?" : "Notiz"} />
      {art === "aufgabe" && (
        <div className="grid tk-crm-aufgabe">
          <label>Fällig am<input type="date" value={faellig} onChange={(e) => setFaellig(e.target.value)} /></label>
          <OwnerSelect value={fuer} onChange={setFuer} label="Für" />
        </div>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row"><button type="submit" className="primary" disabled={!!busy || !text.trim()} aria-busy={busy === "erfassen"}>{busy === "erfassen" ? "Wird gespeichert …" : `${artLabel(art)} speichern`}</button></div>
    </form>
  );
}

/** Verlauf: Systemeinträge, Kontakte, Notizen und Aufgaben (abhaken), eigene Einträge löschen */
export function Verlauf({ eintraege, staff, max }: { eintraege: readonly ActivityRow[]; staff: readonly StaffKurz[] | undefined; max?: number }) {
  const { busy, run } = useAction();
  const frage = useRueckfrage();
  const ich = useIch();
  const { account } = useAccount();
  const admin = account?.platformRole === "admin" || account?.platformRole === "developer";
  const liste = max ? eintraege.slice(0, max) : eintraege;
  if (eintraege.length === 0) return <p className="muted">Noch kein Eintrag.</p>;
  return (
    <ol className="tk-crm-verlauf">
      {liste.map((a) => (
        <li key={a.id} className={`tk-crm-eintrag tk-crm-${a.kind}${a.done_at ? " tk-crm-erledigt" : ""}`}>
          <div className="tk-crm-eintrag-kopf">
            <span className="pill">{artLabel(a.kind)}{a.for_admin ? " · an den Admin" : ""}</span>
            <small>{date(a.created_at)} {time(a.created_at)} · {a.author_id ? staffName(staff, a.author_id) : "System"}</small>
          </div>
          <p className="tk-umbruch">{a.body}</p>
          {a.kind === "aufgabe" && (
            <div className="row tk-crm-aufgabe-zeile">
              <label className="row tk-check">
                <input type="checkbox" checked={!!a.done_at} disabled={!!busy} onChange={(e) => void run(`erledigt-${a.id}`, () => taskDone(api, a.id, e.target.checked), e.target.checked ? "Aufgabe erledigt." : "Aufgabe wieder offen.")} />
                {a.done_at ? `erledigt ${date(a.done_at)}` : "erledigt"}
              </label>
              <small>{a.due_at ? `fällig ${date(a.due_at)}` : "ohne Datum"}{a.for_admin ? "" : ` · für ${staffName(staff, a.assignee_id)}`}</small>
            </div>
          )}
          {a.kind !== "system" && (a.author_id === ich || admin) && (
            <button type="button" className="tk-link-knopf" disabled={!!busy} onClick={() => void (async () => {
              if (await frage.ask({ title: "Eintrag löschen?", text: "Der Eintrag verschwindet aus dem Verlauf.", confirmLabel: "Löschen", danger: true })) {
                await run(`weg-${a.id}`, () => deleteActivity(api, a.id), "Eintrag gelöscht.");
              }
            })()}>Löschen</button>
          )}
        </li>
      ))}
      {frage.element}
    </ol>
  );
}

/** Rabatt nur vorschlagen: Aufgabe an den Admin (Inhaber 02.11.2026) */
export function RabattVorschlag({ leadId }: { leadId: string }) {
  const [offen, setOffen] = useState(false);
  const { busy, error, run } = useAction();
  const [prozent, setProzent] = useState("10");
  const [monate, setMonate] = useState("3");
  const [grund, setGrund] = useState("");
  async function absenden(e: FormEvent) {
    e.preventDefault();
    const m = monate.trim() === "" ? null : Math.trunc(Number(monate));
    const ok = await run("rabatt", () => proposeDiscount(api, leadId, Math.trunc(Number(prozent)), m, grund).then(() => true), "Vorschlag an den Admin geschickt.");
    if (ok) { setOffen(false); setGrund(""); }
  }
  return (
    <>
      <button type="button" onClick={() => setOffen(true)}>Rabatt vorschlagen</button>
      <Dialog open={offen} onClose={() => setOffen(false)} title="Rabatt vorschlagen">
        <form className="stack" onSubmit={(e) => void absenden(e)}>
          <p className="muted">Der Admin sieht den Vorschlag auf seiner Startseite und legt bei Zustimmung den Rabattcode an. Zugesagt ist damit noch nichts.</p>
          <div className="grid tk-crm-aufgabe">
            <label>Rabatt in Prozent<input type="number" inputMode="numeric" min={1} max={100} value={prozent} onChange={(e) => setProzent(e.target.value)} /></label>
            <label>Laufzeit in Monaten<input type="number" inputMode="numeric" min={1} max={36} value={monate} onChange={(e) => setMonate(e.target.value)} placeholder="leer = einmalig" /></label>
          </div>
          <label>Begründung<textarea rows={3} maxLength={1000} value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Warum hilft der Rabatt beim Abschluss?" /></label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="submit" className="primary" disabled={!!busy} aria-busy={busy === "rabatt"}>Vorschlag schicken</button>
            <button type="button" onClick={() => setOffen(false)}>Abbrechen</button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

/** Filter „Zuständig“: alle, meine, ohne Zuständigen oder eine Person */
export function ZustaendigFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const staff = useStaff();
  return (
    <select aria-label="Zuständig" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="alle">Alle Zuständigen</option>
      <option value="meine">Meine</option>
      <option value="ohne">Ohne Zuständigen</option>
      {(staff.data ?? []).filter((s) => s.active).map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
    </select>
  );
}
